/**
 * Pure helpers for the bulk (select-many) user editor.
 *
 * The Desa+ proxy has no bulk endpoint — only `POST /edit-user`, which requires
 * the complete user object per call. Bulk editing therefore fans out one
 * edit-user request per selected user, overriding only the fields the operator
 * opted into and copying everything else from the source user unchanged.
 */

/** Subset of a user row needed to build a full edit-user payload. */
export interface BulkEditableUser {
  id: string
  name: string
  nik: string
  phone: string
  email: string
  gender: string
  idUserRole: string
  idVillage: string
  idGroup: string
  idPosition: string
  isActive: boolean
  isWithoutOTP: boolean
  isApprover: boolean
}

/** Exact body shape accepted by the edit-user endpoint. */
export interface EditUserPayload extends BulkEditableUser {}

/**
 * Fields that are safe to edit across many users at once. Unique fields
 * (name/NIK/phone/email) are excluded — the endpoint rejects duplicates.
 */
export const BULK_FIELDS = [
  { key: 'isActive', label: 'Status', description: 'Set account active or inactive' },
  { key: 'isWithoutOTP', label: 'Without OTP', description: 'Bypass login OTP verification' },
  { key: 'isApprover', label: 'Approver', description: 'Grant approver privileges' },
] as const

/** Partial set of opted-in changes; a key is present only when its toggle is on. */
export type BulkEditChanges = Partial<Pick<BulkEditableUser, 'isActive' | 'isWithoutOTP' | 'isApprover'>>

/**
 * Build the full edit-user payload for a single user, overriding only the
 * fields present in `changes`. All other fields (including the unique
 * nik/phone/email) are preserved from the source user.
 */
export function buildBulkEditPayload(user: BulkEditableUser, changes: BulkEditChanges): EditUserPayload {
  return {
    id: user.id,
    name: user.name,
    nik: user.nik,
    phone: user.phone,
    email: user.email,
    gender: user.gender,
    idUserRole: user.idUserRole,
    idVillage: user.idVillage,
    idGroup: user.idGroup,
    idPosition: user.idPosition,
    isActive: changes.isActive ?? user.isActive,
    isWithoutOTP: changes.isWithoutOTP ?? user.isWithoutOTP,
    isApprover: changes.isApprover ?? user.isApprover,
  }
}

/** Build edit-user payloads for every selected user. */
export function buildBulkEditPayloads(users: BulkEditableUser[], changes: BulkEditChanges): EditUserPayload[] {
  return users.map((user) => buildBulkEditPayload(user, changes))
}
